import { useEffect, useMemo, useState } from "react";
import { Check, ChevronsUpDown, Tags, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  ApiError,
  formatWecomTagLabel,
  listWecomExternalContactTags,
  type WecomTagOption,
} from "@/lib/wecom-external-contacts";

function tagLabel(tag: WecomTagOption): string {
  return formatWecomTagLabel(tag);
}

function selectedTagsSummary(
  ids: string[],
  options: WecomTagOption[],
  maxNames = 2,
): { text: string; title: string } {
  const names = ids.map((id) => {
    const tag = options.find((t) => t.id === id);
    return tag ? tagLabel(tag) : id;
  });
  const full = names.join("、");
  if (names.length <= maxNames) {
    return { text: full || "全部标签", title: full };
  }
  return {
    text: `${names.slice(0, maxNames).join("、")} 等`,
    title: full,
  };
}

/** 企微标签多选：选项来自账户内已同步出现过的标签 */
export function WecomTagMultiSelect({
  accountId,
  value,
  onChange,
  disabled = false,
  className,
}: {
  accountId: string | number;
  value: string[];
  onChange: (tagIds: string[]) => void;
  disabled?: boolean;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<WecomTagOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    void listWecomExternalContactTags(accountId)
      .then((tags) => {
        if (!cancelled) setOptions(tags);
      })
      .catch((caught) => {
        if (!cancelled) {
          setLoadError(caught instanceof ApiError ? caught.message : "无法加载标签");
          setOptions([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  const summary = useMemo(
    () => selectedTagsSummary(value, options),
    [value, options],
  );

  function toggle(id: string) {
    if (value.includes(id)) {
      onChange(value.filter((x) => x !== id));
    } else {
      onChange([...value, id]);
    }
  }

  return (
    <div className={cn("flex min-w-0 items-center gap-1", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-label="按企微标签筛选"
            disabled={disabled}
            className="h-9 min-w-0 flex-1 justify-between font-normal"
          >
            <span className="flex min-w-0 items-center gap-1.5">
              <Tags className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
              <span className="truncate" title={summary.title || undefined}>
                {value.length === 0 ? "全部标签" : summary.text}
              </span>
            </span>
            <ChevronsUpDown className="size-3.5 shrink-0 opacity-50" aria-hidden />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[min(100vw-2rem,24rem)] p-0"
          align="start"
        >
          <Command>
            <CommandInput placeholder="搜索标签…" />
            <CommandList>
              <CommandEmpty>
                {loading ? "加载中…" : loadError || "暂无已同步的标签"}
              </CommandEmpty>
              <CommandGroup>
                {options.map((tag) => {
                  const selected = value.includes(tag.id);
                  const label = tagLabel(tag);
                  return (
                    <CommandItem
                      key={tag.id}
                      value={`${label} ${tag.id}`}
                      onSelect={() => toggle(tag.id)}
                      className="items-start"
                    >
                      <Check
                        className={cn(
                          "mt-0.5 mr-2 size-3.5 shrink-0",
                          selected ? "opacity-100" : "opacity-0",
                        )}
                        aria-hidden
                      />
                      <span className="min-w-0 flex-1 whitespace-normal break-words leading-snug">
                        {label}
                      </span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {value.length > 0 ? (
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="size-8 shrink-0"
          disabled={disabled}
          aria-label="清除标签筛选"
          onClick={() => onChange([])}
        >
          <X className="size-3.5" aria-hidden />
        </Button>
      ) : null}
    </div>
  );
}
